import { useState, type FormEvent } from "react";
import { PROPERTY_TYPES, type PropertyType } from "../../constants";

interface PropertyMetricStepProps {
  onSubmit: (sqFt: number, propertyType: PropertyType) => void;
}

export function PropertyMetricStep({ onSubmit }: PropertyMetricStepProps) {
  const [sqFt, setSqFt] = useState("");
  const [propertyType, setPropertyType] = useState<PropertyType | null>(null);
  const [error, setError] = useState("");

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const parsed = parseInt(sqFt.replace(/\D/g, ""), 10);
    if (!parsed || parsed < 200 || parsed > 50000) {
      setError("Enter a valid square footage between 200 and 50,000.");
      return;
    }
    if (!propertyType) {
      setError("Select a property type to continue.");
      return;
    }
    setError("");
    onSubmit(parsed, propertyType);
  }

  return (
    <div className="p-4 sm:p-5">
      <div className="mb-4 border-l-2 border-emerald-600 pl-3">
        <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">Metric 1</p>
        <h2 className="text-base font-semibold text-slate-900">Structural Size &amp; Property Type</h2>
        <p className="mt-0.5 text-xs text-slate-500">Input area dimensions to calibrate treatment volume estimates.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="sqft" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-600">
            Approximate Square Footage
          </label>
          <div className="relative">
            <input
              id="sqft"
              type="text"
              inputMode="numeric"
              placeholder="e.g. 1800"
              value={sqFt}
              onChange={e => {
                setSqFt(e.target.value.replace(/\D/g, ""));
                if (error) setError("");
              }}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 pr-12 font-mono text-lg text-slate-900 placeholder:text-slate-300 focus:border-emerald-600 focus:outline-none focus:ring-1 focus:ring-emerald-600"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-slate-400">sq ft</span>
          </div>
        </div>

        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">Property Type</p>
          <div className="grid gap-2">
            {PROPERTY_TYPES.map(type => (
              <button
                key={type.id}
                type="button"
                onClick={() => {
                  setPropertyType(type.id);
                  if (error) setError("");
                }}
                className={`rounded-lg border px-3 py-2.5 text-left transition-all ${
                  propertyType === type.id
                    ? "border-emerald-600 bg-emerald-50 ring-1 ring-emerald-600"
                    : "border-slate-200 bg-white hover:border-slate-300"
                }`}
              >
                <p className="text-sm font-semibold text-slate-900">{type.label}</p>
                <p className="text-xs text-slate-500">{type.description}</p>
              </button>
            ))}
          </div>
        </div>

        {error && <p className="text-xs text-red-600">{error}</p>}

        <button
          type="submit"
          className="w-full rounded-lg bg-slate-900 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-slate-800"
        >
          Continue to Location Data
        </button>
      </form>
    </div>
  );
}
